import { Box, Typography } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useWatch } from 'react-hook-form';

import { CustomTooltip } from 'src/components/tooltip';
import { ControlledField, FieldTypes } from 'src/components/controlled-field';
import { TextFieldTypes } from 'src/components/controlled-field/types';
import { moveToTop } from 'src/utils/sort-labels';
import {
    selectUserHasBookings,
    selectUserHasSpanishResidenceInfo,
} from 'src/store/slices/user-slice';

import {
    countries,
    defaultOption,
    languages,
    nationalities,
    universitiesCountryMap,
    userDataKeys,
    studyYears,
} from './constants';
import { AddressDetailsProps, UserDataFormValues } from './types';
import { styles } from './styles';

export const AddressDetails: FC<AddressDetailsProps<UserDataFormValues>> = ({ control }) => {
    const { t } = useTranslation();

    const nifNumber = useWatch({ control, name: 'nifNumber' });
    const nieNumber = useWatch({ control, name: 'nieNumber' });
    const passportNumber = useWatch({ control, name: 'passportNumber' });
    const passportIssuingCountry = useWatch({ control, name: 'passportIssuingCountry' });

    const userHasBookings = useSelector(selectUserHasBookings);
    const userSpanishTaxInfo = useSelector(selectUserHasSpanishResidenceInfo);

    const isOccupantsPage = window.location.href.includes('occupants');

    const userHasSpanishResidenceInfo =
        userSpanishTaxInfo ||
        (isOccupantsPage &&
            (!!nifNumber || !!nieNumber || (!!passportNumber && !!passportIssuingCountry)));

    const universities = [
        defaultOption,
        ...universitiesCountryMap.en,
        ...universitiesCountryMap.es,
    ];

    const sortedNationalities = [
        defaultOption,
        ...moveToTop(t, nationalities, ['nationalities_british']),
    ];

    const sortedCountries = [
        defaultOption,
        ...moveToTop(t, countries, ['countries_united_kingdom']),
    ];

    return (
        <Box css={styles.addressDetailsContainer}>
            <Box css={styles.addressSubtitle}>
                <Typography variant="h4">
                    {t('your_details_form_permanent_address_subtitle')}
                </Typography>
                <CustomTooltip
                    title={
                        <Typography variant="body4">
                            {t('your_details_form_permanent_address_info')}
                        </Typography>
                    }
                />
            </Box>

            <ControlledField
                name={userDataKeys.address1}
                control={control}
                label={t('your_details_form_address_1_label')}
                placeholder={t('your_details_form_address_1_placeholder')}
                fullWidth
                disabled={userHasBookings && userHasSpanishResidenceInfo}
                required
            />
            <ControlledField
                name={userDataKeys.address2}
                control={control}
                label={t('your_details_form_address_2_label')}
                placeholder={t('your_details_form_address_2_placeholder')}
                fullWidth
                disabled={userHasBookings && userHasSpanishResidenceInfo}
                required
            />
            <ControlledField
                name={userDataKeys.city}
                control={control}
                label={t('your_details_form_city_label')}
                placeholder={t('your_details_form_city_placeholder')}
                fullWidth
                disabled={userHasBookings && userHasSpanishResidenceInfo}
                required
            />

            <Box css={styles.inputGroupWrapper}>
                <ControlledField
                    name={userDataKeys.postCode}
                    control={control}
                    type={TextFieldTypes.POSTCODE}
                    label={t('your_details_form_post_code_label')}
                    placeholder={t('your_details_form_post_code_placeholder')}
                    fullWidth
                    disabled={userHasBookings && userHasSpanishResidenceInfo}
                    required
                />
                <ControlledField
                    fieldType={FieldTypes.Select}
                    name={userDataKeys.country}
                    control={control}
                    label={t('your_details_form_country_label')}
                    placeholder={t('your_details_form_country_placeholder')}
                    fullWidth
                    disabled={userHasBookings && userHasSpanishResidenceInfo}
                    required
                    options={sortedCountries}
                />
            </Box>
            <Box css={styles.inputGroupWrapper}>
                <ControlledField
                    fieldType={FieldTypes.Select}
                    name={userDataKeys.nationality}
                    control={control}
                    options={sortedNationalities}
                    label={t('your_details_form_nationality_label')}
                />
                <ControlledField
                    fieldType={FieldTypes.Select}
                    name={userDataKeys.preferredLanguage}
                    control={control}
                    options={languages}
                    label={t('your_details_form_language_label')}
                />
            </Box>
            <Box css={styles.inputGroupWrapper}>
                <ControlledField
                    fieldType={FieldTypes.Select}
                    name={userDataKeys.university}
                    control={control}
                    options={universities}
                    label={t('your_details_form_university_label')}
                />
                <ControlledField
                    fieldType={FieldTypes.Select}
                    name={userDataKeys.studyYear}
                    control={control}
                    options={studyYears}
                    label={t('your_details_form_year_of_study_label')}
                />
            </Box>
        </Box>
    );
};
